import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  toApiError,
  useApi,
  type GuestCommunicationMessageKey,
  type PropertyCommunicationCatalogResponse,
} from '@/shared/api';
import { toUpdateRequest, toggleMessage } from './catalogModel';
import { problemReason, unanswered } from './catalogProblem';

export interface SwitchChange {
  key: GuestCommunicationMessageKey;
  enabled: boolean;
}

export type SwitchOutcome =
  | ({ kind: 'saved' } & SwitchChange)
  | ({
      kind: 'failed';
      conflict: boolean;
      /** Keine Antwort: ob der Schalter gespeichert wurde, ist offen. */
      unclear: boolean;
      reason: string;
    } & SwitchChange);

/**
 * Ein Schalter wirkt sofort. Gespeichert wird der gespeicherte Stand mit genau diesem Schalter und
 * gegen die geladene Revision — nie die ungespeicherten Texte des Entwurfs. Scheitert es, zeigt der
 * Schalter wieder den gespeicherten Stand und die Rückmeldung nennt den Grund.
 */
export function useCatalogSwitch(
  groupId: string,
  baseline: PropertyCommunicationCatalogResponse | null,
  onSaved: (saved: PropertyCommunicationCatalogResponse) => void,
) {
  const api = useApi();
  const [outcome, setOutcome] = useState<SwitchOutcome | null>(null);
  const mutation = useMutation({
    mutationFn: ({ key, enabled }: SwitchChange) =>
      api.propertyGroups.guestExperience.messages.update(
        groupId,
        toUpdateRequest(toggleMessage(baseline!, key, enabled)),
      ),
    // Offline pausiert TanStack sonst still; so kommt der Grund sofort. Die letzte Rückmeldung
    // bleibt bis zur nächsten stehen, damit der Fokus auf „Rückgängig“ nicht ins Leere fällt.
    networkMode: 'always',
    onSuccess: (saved, change) => {
      onSaved(saved);
      setOutcome({ kind: 'saved', ...change });
    },
    onError: async (error, change) => {
      const problem = await toApiError(error);
      setOutcome({
        kind: 'failed',
        ...change,
        conflict: problem.status === 409,
        unclear: unanswered(problem),
        reason: problemReason(problem, 'Der Server hat die Änderung nicht übernommen.'),
      });
    },
  });

  return {
    /** Der Wechsel, der gerade gespeichert wird — der Schalter zeigt ihn schon an. */
    pending: mutation.isPending ? mutation.variables : undefined,
    outcome,
    change: (change: SwitchChange) => mutation.mutate(change),
    undo: () => {
      if (outcome?.kind === 'saved')
        mutation.mutate({ key: outcome.key, enabled: !outcome.enabled });
    },
    clear: () => setOutcome(null),
  };
}
